import React, { lazy, Suspense } from 'react';
import Banner from '@/pages/HomePage/components/Banner';
import ViewportLazyMount from '@/components/common/ViewportLazyMount';
import { HomePageSkeletonSection } from '@/components/HomePageSkeleton';
import type { HomePageSkeletonSectionType } from '@/components/HomePageSkeleton';
import styles from '@/pages/HomePage/index.module.scss';

const Recomend = lazy(() => import('@/pages/HomePage/components/Recomended'));
const Categories = lazy(() => import('@/pages/HomePage/components/categories/categories'));
const SpecialEffectCards = lazy(() => import('@/pages/HomePage/components/SpecialEffectCards'));
const ThemeZone = lazy(() => import('@/pages/HomePage/components/ThemeZone'));
const FeaturedProducts = lazy(() => import('@/pages/HomePage/components/FeaturedProducts'));

export interface ModuleItem {
  moduleType: string;
  moduleId: number;
  priority: number;
  bigBannerConfig?: any;
  zoneBannerConfig?: any;
  recommendProductConfig?: any;
  recommendCategoryConfig?: any;
  specialEffectCardConfig?: any;
  themeZoneConfig?: any;
  recommendProductTwoConfig?: any;
  featuredProductConfig?: any;
  selectedProductConfig?: any;
  selectionProductConfig?: any;
  productSelectionConfig?: any;
}

const BelowFoldModule: React.FC<{
  moduleId: number;
  skeletonType: HomePageSkeletonSectionType;
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}> = ({ moduleId, skeletonType, className, style, children }) => (
  <div style={style} className={className}>
    <ViewportLazyMount fallback={<HomePageSkeletonSection type={skeletonType} />}>
      {children}
    </ViewportLazyMount>
  </div>
);

const getMarginTop = (moduleType: string) => {
  switch (moduleType) {
    case '10100-01':
    case '10100-02':
    case '10100-03':
    case '10100-04':
    case '10100-05':
    case '10100-06':
    case '10100-07':
      return '0px';
    default:
      return '0px';
  }
};

export const renderModuleItem = (moduleItem: ModuleItem) => {
  if (!moduleItem?.moduleType || !moduleItem?.moduleId) {
    console.warn('跳过缺少模块标识的首页配置:', moduleItem);
    return null;
  }

  const requiredConfigMap: Record<string, keyof ModuleItem> = {
    '10100-01': 'bigBannerConfig',
    '10100-02': 'zoneBannerConfig',
    '10100-03': 'recommendProductConfig',
    '10100-04': 'recommendCategoryConfig',
    '10100-05': 'specialEffectCardConfig',
    '10100-06': 'themeZoneConfig',
    // 接口返回 recommendProductTwoConfig。featuredProductConfig 只作为历史别名。
    '10100-07': 'recommendProductTwoConfig',
  };
  const requiredConfigKey = requiredConfigMap[moduleItem.moduleType];
  // 精选商品接口字段是 recommendProductTwoConfig，历史数据可能仍挂在 featuredProductConfig。
  const hasRequiredConfig =
    moduleItem.moduleType === '10100-07'
      ? Boolean(moduleItem.recommendProductTwoConfig || moduleItem.featuredProductConfig)
      : !requiredConfigKey || Boolean(moduleItem[requiredConfigKey]);
  if (!hasRequiredConfig) {
    // CMS 单个楼层脏数据只能影响自身，不能让首页其它有效模块一起白屏。
    console.warn('跳过缺少必要配置的首页模块:', moduleItem.moduleType, moduleItem.moduleId);
    return null;
  }

  const moduleStyle = { marginTop: getMarginTop(moduleItem.moduleType) };

  switch (moduleItem.moduleType) {
    case '10100-01':
      return (
        <div key={moduleItem.moduleId} style={moduleStyle} className={styles.headBannerController}>
          <Banner data={moduleItem} type="big" />
        </div>
      );
    case '10100-02':
      return (
        <BelowFoldModule
          key={moduleItem.moduleId}
          moduleId={moduleItem.moduleId}
          skeletonType="zoneBanner"
          style={moduleStyle}
        >
          <div className={styles.marginController}>
            <Banner data={moduleItem} type="zone" />
          </div>
        </BelowFoldModule>
      );
    case '10100-03':
      if (!Array.isArray(moduleItem.recommendProductConfig?.products)) {
        console.warn('跳过商品推荐配置异常的首页模块:', moduleItem.moduleId);
        return null;
      }
      return (
        <BelowFoldModule
          key={moduleItem.moduleId}
          moduleId={moduleItem.moduleId}
          skeletonType="recommend"
          style={moduleStyle}
          className={styles.marginController}
        >
          <Suspense fallback={<HomePageSkeletonSection type="recommend" />}>
            <Recomend
              title={moduleItem.recommendProductConfig.title}
              type="config"
              products={moduleItem.recommendProductConfig.products}
              recommendProductConfig={moduleItem.recommendProductConfig}
            />
          </Suspense>
        </BelowFoldModule>
      );
    case '10100-04':
      if (!Array.isArray(moduleItem.recommendCategoryConfig?.categories)) {
        console.warn('跳过分类推荐配置异常的首页模块:', moduleItem.moduleId);
        return null;
      }
      return (
        <BelowFoldModule
          key={moduleItem.moduleId}
          moduleId={moduleItem.moduleId}
          skeletonType="categories"
          style={moduleStyle}
          className={styles.marginControllerCategory}
        >
          <Suspense fallback={<HomePageSkeletonSection type="categories" />}>
            <Categories
              title={moduleItem.recommendCategoryConfig.title}
              categories={moduleItem.recommendCategoryConfig.categories}
            />
          </Suspense>
        </BelowFoldModule>
      );
    case '10100-05':
      return (
        <BelowFoldModule
          key={moduleItem.moduleId}
          moduleId={moduleItem.moduleId}
          skeletonType="specialCards"
          style={moduleStyle}
          className={styles.topBannerController}
        >
          <Suspense fallback={<HomePageSkeletonSection type="specialCards" />}>
            <SpecialEffectCards data={{ ...moduleItem, specialEffectCardConfig: moduleItem.specialEffectCardConfig }} />
          </Suspense>
        </BelowFoldModule>
      );
    case '10100-06':
      return (
        <BelowFoldModule
          key={moduleItem.moduleId}
          moduleId={moduleItem.moduleId}
          skeletonType="themeZone"
          style={moduleStyle}
          className={styles.marginController}
        >
          <Suspense fallback={<HomePageSkeletonSection type="themeZone" />}>
            <ThemeZone data={{ ...moduleItem, themeZoneConfig: moduleItem.themeZoneConfig }} />
          </Suspense>
        </BelowFoldModule>
      );
    case '10100-07':
      return (
        <BelowFoldModule
          key={moduleItem.moduleId}
          moduleId={moduleItem.moduleId}
          skeletonType="featured"
          style={moduleStyle}
        >
          <Suspense fallback={<HomePageSkeletonSection type="featured" />}>
            <FeaturedProducts data={moduleItem} />
          </Suspense>
        </BelowFoldModule>
      );
    default:
      return null;
  }
};

